import { Plane, User } from '@/components/icons'
import { AlertBadge } from '@/components/ui/alert-badge'
import { Link } from '@tanstack/react-router'
import { cn, getInitials } from '@/lib/utils'
import { Card } from '@/components/ui/card'
import { formatFlightDuration, formatLocalTime, formatLocalDate } from '@/lib/flight-utils'
import { getAirportByIcao } from '@/lib/airport-db'

export interface FlightListItem {
  id: string
  tripId?: string
  departureAirportIcao: string
  arrivalAirportIcao: string
  departureDateTime: string | Date
  arrivalDateTime: string | Date
  aircraftRegistration?: string | null
  crew?: { id: string; firstName: string; lastName: string; avatarColor: string }[]
  passengers?: { id: string; firstName: string; lastName: string; avatarColor: string }[]
  /** Passengers whose identity is not yet known (count only) */
  unknownPaxCount?: number
}

function AvatarStack({ people, greyOut = false, unknownCount = 0 }: { people: { id: string; firstName: string; lastName: string; avatarColor: string }[]; greyOut?: boolean; unknownCount?: number }) {
  const maxVisible = 3
  const visible = people.slice(0, maxVisible)
  const remaining = people.length - maxVisible

  return (
    <div className="flex items-center pr-1">
      {visible.map((person) => (
        <div
          key={person.id}
          className={cn("size-6 -mr-1 rounded-full flex items-center justify-center text-xs font-medium text-content-primary-inverse", greyOut && "grayscale")}
          style={{ backgroundColor: person.avatarColor }}
        >
          {getInitials(person.firstName, person.lastName)}
        </div>
      ))}
      {unknownCount > 0 && (
        <div className={cn('size-6 -mr-1 rounded-full flex items-center justify-center bg-content-primary-subtle-mid', greyOut && 'grayscale')}>
          <User className="size-3.5 text-content-primary-inverse" />
        </div>
      )}
      {remaining > 0 && (
        <div className="size-6 -mr-1 rounded-full flex items-center justify-center text-xs font-medium bg-surface-L2 text-content-primary-subtle">
          +{remaining}
        </div>
      )}
    </div>
  )
}

export function HighlightMatch({ text, query }: { text: string; query?: string }) {
  if (!query || !text) return <>{text}</>
  const idx = text.toLowerCase().indexOf(query.toLowerCase())
  if (idx === -1) return <>{text}</>
  return (
    <>
      {text.slice(0, idx)}
      <span className="bg-background-brand-bold/20 text-content-brand-bold rounded-sm">{text.slice(idx, idx + query.length)}</span>
      {text.slice(idx + query.length)}
    </>
  )
}

interface FlightListProps {
  flights: FlightListItem[]
  onFlightClick?: (flightId: string) => void
  emptyMessage?: string
  disabled?: boolean
  /** Renders flights without individual cards, using dashed separators instead */
  compact?: boolean
  /** When true, past flights (arrivalDateTime < now) are separated below a "Complete" label */
  splitPastFlights?: boolean
  /** When true, hides the Crew + PAX row */
  hideCrewPax?: boolean
  /** When provided, matching text in airport codes and municipalities is highlighted */
  searchQuery?: string
  /** Route a row with a tripId links to (when no onFlightClick is provided) */
  tripLinkTo?: '/operations/trips/$tripId' | '/schedule/$tripId'
}

export function FlightList({
  flights,
  onFlightClick,
  emptyMessage = 'No flights',
  disabled = false,
  compact = false,
  splitPastFlights = false,
  hideCrewPax = false,
  searchQuery,
  tripLinkTo = '/operations/trips/$tripId',
}: FlightListProps) {
  if (!flights || flights.length === 0) {
    return (
      <p className="text-content-primary-subtle text-sm text-center py-4">
        {emptyMessage}
      </p>
    )
  }

  const renderFlightContent = (flight: FlightListItem) => {
    const hasCrew = flight.crew && flight.crew.length > 0
    const hasPax = flight.passengers && flight.passengers.length > 0
    const depAirport = getAirportByIcao(flight.departureAirportIcao)
    const arrAirport = getAirportByIcao(flight.arrivalAirportIcao)

    return (
      <div className="flex flex-col gap-3">
        {/* Flight route row - 3 column layout */}
        <div className="flex items-center">
          {/* Departure */}
          <div className="flex flex-col items-start flex-1 min-w-0">
            <span className="text-content-primary-bold text-base font-semibold leading-none">
              <HighlightMatch text={flight.departureAirportIcao} query={searchQuery} />
            </span>
            {depAirport?.municipality && (
              <span className="text-xs font-medium text-content-primary-subtle leading-normal tracking-[0.18px] truncate max-w-full">
                <HighlightMatch text={depAirport.municipality} query={searchQuery} />
              </span>
            )}
            <div className="flex items-center gap-2 text-xs text-content-primary-bold leading-normal tracking-[0.18px]">
              <span>{formatLocalDate(flight.departureDateTime, depAirport?.timezone)}</span>
              <span>{formatLocalTime(flight.departureDateTime, depAirport?.timezone)}</span>
            </div>
          </div>

          {/* Center - plane icon + duration */}
          <div className="flex flex-col items-center shrink-0 px-2">
            <Plane className="size-5 text-content-primary-subtle rotate-45" />
            <span className="text-xs font-medium text-content-primary-bold tracking-[0.18px]">
              {formatFlightDuration(flight.departureDateTime, flight.arrivalDateTime)}
            </span>
            {flight.aircraftRegistration && (
              <span className="text-xs font-medium text-content-primary-subtle tracking-[0.18px]">
                {flight.aircraftRegistration}
              </span>
            )}
          </div>

          {/* Arrival */}
          <div className="flex flex-col items-end flex-1 min-w-0">
            <span className="text-content-primary-bold text-base font-semibold leading-none">
              <HighlightMatch text={flight.arrivalAirportIcao} query={searchQuery} />
            </span>
            {arrAirport?.municipality && (
              <span className="text-xs font-medium text-content-primary-subtle leading-normal tracking-[0.18px] truncate max-w-full">
                <HighlightMatch text={arrAirport.municipality} query={searchQuery} />
              </span>
            )}
            <div className="flex items-center gap-2 text-xs text-content-primary-bold leading-normal tracking-[0.18px]">
              <span>{formatLocalDate(flight.arrivalDateTime, arrAirport?.timezone)}</span>
              <span>{formatLocalTime(flight.arrivalDateTime, arrAirport?.timezone)}</span>
            </div>
          </div>
        </div>

        {/* Crew + PAX row */}
        {!hideCrewPax && (
        <div className="flex items-center border-t border-border-L2 pt-3">
          <div className="flex-1 flex items-center gap-2">
            <span className="text-content-primary-bold text-sm font-medium">Crew</span>
            {hasCrew ? (
              <AvatarStack people={flight.crew!} greyOut={disabled} />
            ) : (
              <AlertBadge variant="warning" />
            )}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-content-primary-bold text-sm font-medium">PAX</span>
            {hasPax ? (
              <AvatarStack people={flight.passengers!} greyOut={disabled} />
            ) : (
              <AlertBadge variant="warning" />
            )}
          </div>
        </div>
        )}
      </div>
    )
  }

  if (compact) {
    return (
      <div className={cn('flex flex-col gap-3', disabled && 'opacity-70 pointer-events-none')}>
        {flights.map((flight, index) => (
          <div key={flight.id}>
            {index > 0 && (
              <div className="border-t border-dashed border-border-L1 mb-3" />
            )}
            {flight.tripId && !onFlightClick ? (
              <Link
                to={tripLinkTo}
                params={{ tripId: flight.tripId }}
                search={{} as any}
                className="block"
              >
                {renderFlightContent(flight)}
              </Link>
            ) : onFlightClick ? (
              <button
                onClick={() => onFlightClick(flight.id)}
                className="w-full text-left"
              >
                {renderFlightContent(flight)}
              </button>
            ) : (
              renderFlightContent(flight)
            )}
          </div>
        ))}
      </div>
    )
  }

  const renderFlightCard = (flight: FlightListItem, past = false) => (
    <Card key={flight.id} className={cn('p-3', past && 'shadow-none', disabled && 'opacity-70 pointer-events-none')}>
      {flight.tripId && !onFlightClick ? (
        <Link
          to={tripLinkTo}
          params={{ tripId: flight.tripId }}
          search={{} as any}
          className="w-full block"
        >
          {renderFlightContent(flight)}
        </Link>
      ) : (
        <button
          onClick={() => onFlightClick?.(flight.id)}
          disabled={!onFlightClick || disabled}
          className="w-full block text-left disabled:cursor-default"
        >
          {renderFlightContent(flight)}
        </button>
      )}
    </Card>
  )

  if (splitPastFlights) {
    const now = new Date()
    const upcomingFlights = flights.filter((f) => new Date(f.arrivalDateTime) >= now)
    const pastFlights = flights.filter((f) => new Date(f.arrivalDateTime) < now)

    return (
      <div className="flex flex-col gap-2">
        {upcomingFlights.map((flight) => renderFlightCard(flight))}
        {pastFlights.length > 0 && (
          <>
            <div className="flex items-center pt-2">
              <span className="text-xs font-semibold text-content-primary-subtle">Complete</span>
            </div>
            {pastFlights.map((flight) => renderFlightCard(flight, true))}
          </>
        )}
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-2">
      {flights.map((flight) => renderFlightCard(flight))}
    </div>
  )
}
